import { JobStatus, SupabaseJob, Job } from '@applicator/shared'
import { statusToDbFormat } from '@applicator/shared'

import { supabase } from '../../lib/supabase'
import { DateMode } from '../JobDateChips/types'

import { formatDateTime as formatDateTimeLocal } from './formatting'

// Helper function to check if date needs to be set for Applied status
export function needsDateSetForApplied(
  appliedAt: string | null,
  dateAppliedMode: DateMode | undefined,
  dateAppliedDisplay: string | undefined
): boolean {
  return !appliedAt || 
         dateAppliedMode === 'cleared' || 
         !dateAppliedDisplay || 
         dateAppliedDisplay === 'Not set' || 
         dateAppliedDisplay === 'N/A'
}

// Helper function to prepare applied date update data
export function prepareAppliedDateUpdate(): { applied_at: string; date_applied_mode: string; date_applied_manual: string } {
  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  return {
    applied_at: new Date().toISOString(),
    date_applied_mode: 'manual',
    date_applied_manual: todayStr
  }
}

// Helper function to load current job data from Supabase
export async function loadCurrentJobData(jobId: string) {
  const { data: currentJobData } = await supabase
    .from('jobs')
    .select('applied_at, date_applied_mode')
    .eq('id', jobId)
    .single()
  return currentJobData
}

// Helper function to update status with applied date if needed
export async function prepareStatusUpdateData(
  newStatus: JobStatus,
  currentJob: Job | null
): Promise<Record<string, unknown>> {
  const updateData: Record<string, unknown> = {
    status: statusToDbFormat(newStatus),
  }

  if (newStatus === 'Applied' && currentJob?.id) {
    const currentJobData = await loadCurrentJobData(currentJob.id)
    const appliedAt = currentJobData?.applied_at
    const dateAppliedMode = currentJobData?.date_applied_mode as DateMode | undefined
    
    if (needsDateSetForApplied(appliedAt, dateAppliedMode, currentJob.dateApplied)) {
      const dateUpdate = prepareAppliedDateUpdate()
      Object.assign(updateData, dateUpdate)
    }
  }

  return updateData
}

// Helper function to update job with new status optimistically
export function updateJobStatusOptimistically(
  newStatus: JobStatus,
  updateData: Record<string, unknown>,
  jobs: Job[],
  currentJob: Job | null
): Job[] {
  return jobs.map(job => {
    if (job.id === currentJob?.id) {
      const updated = { ...job, status: newStatus }
      if (newStatus === 'Applied' && updateData.applied_at) {
        updated.dateApplied = formatDateTimeLocal(updateData.applied_at as string)
      }
      return updated
    }
    return job
  })
}

// Helper function to get updated applied at value
export function getUpdatedAppliedAt(
  newStatus: JobStatus,
  updateData: Record<string, unknown>,
  currentAppliedAt: string | null
): string | null {
  if (newStatus === 'Applied' && updateData.applied_at) {
    return updateData.applied_at as string
  }
  if (newStatus === 'Applied') {
    return new Date().toISOString()
  }
  return currentAppliedAt
}

// Helper function to get updated date applied mode
export function getUpdatedDateAppliedMode(
  newStatus: JobStatus,
  updateData: Record<string, unknown>,
  currentMode: string | null
): string | null {
  if (newStatus === 'Applied' && updateData.date_applied_mode) {
    return updateData.date_applied_mode as string
  }
  return currentMode
}

// Helper function to get updated date applied manual
export function getUpdatedDateAppliedManual(
  newStatus: JobStatus,
  updateData: Record<string, unknown>,
  currentManual: string | null
): string | null {
  if (newStatus === 'Applied' && updateData.date_applied_manual) {
    return updateData.date_applied_manual as string
  }
  return currentManual
}

// Helper function to update single supabase job after status change
export function updateSingleSupabaseJob(
  sbJob: SupabaseJob,
  newStatus: JobStatus,
  updateData: Record<string, unknown>
): SupabaseJob {
  const updatedStatus = statusToDbFormat(newStatus)
  const updatedAppliedAt = getUpdatedAppliedAt(newStatus, updateData, sbJob.applied_at)
  const updatedDateAppliedMode = getUpdatedDateAppliedMode(newStatus, updateData, sbJob.date_applied_mode)
  const updatedDateAppliedManual = getUpdatedDateAppliedManual(newStatus, updateData, sbJob.date_applied_manual)
  
  return {
    ...sbJob,
    status: updatedStatus,
    applied_at: updatedAppliedAt,
    date_applied_mode: updatedDateAppliedMode,
    date_applied_manual: updatedDateAppliedManual,
  }
}

// Helper function to update supabaseJobs after status change
export function updateSupabaseJobsAfterStatusChange(
  newStatus: JobStatus,
  updateData: Record<string, unknown>,
  supabaseJobs: SupabaseJob[],
  currentJob: Job | null,
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
) {
  setSupabaseJobs((prevSupabaseJobs) =>
    prevSupabaseJobs.map(sbJob =>
      sbJob.id === currentJob?.id ? updateSingleSupabaseJob(sbJob, newStatus, updateData) : sbJob
    )
  )
}

// Helper function to update jobs state after status change
export function updateJobsStateAfterStatusChange(
  newStatus: JobStatus,
  updateData: Record<string, unknown>,
  jobs: Job[],
  currentJob: Job | null,
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
) {
  setJobs((prevJobs) => 
    prevJobs.map(job => 
      job.id === currentJob?.id 
        ? { 
            ...job, 
            status: newStatus, 
            dateApplied: newStatus === 'Applied' && updateData.applied_at 
              ? formatDateTimeLocal(updateData.applied_at as string) 
              : job.dateApplied 
          }
        : job
    )
  )
}
